import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowRight,
  Star,
  TrendingUp,
  Target,
  CheckCircle,
  Calendar,
  Briefcase
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface CareerPath {
  id: string;
  title: string;
  description: string;
  matchScore: number;
  timeframe: string;
  skills: string[];
  milestones: string[];
  salary?: {
    min: number;
    max: number;
    currency: string;
  };
}

interface RecommendedPathsProps {
  paths: CareerPath[];
  onExplore?: (pathId: string) => void;
}

export function RecommendedPaths({ paths, onExplore }: RecommendedPathsProps) {
  const formatSalary = (min: number, max: number, currency: string = 'USD') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    });
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Recommended Career Paths</h2>
        <Button variant="outline" size="sm">
          <Star className="h-4 w-4 mr-2" />
          View All Paths
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paths.map((path) => (
          <Card key={path.id} className="relative group">
            {path.matchScore >= 80 && (
              <div className="absolute -top-2 left-4 z-10">
                <Badge className="bg-primary">
                  <Star className="h-3 w-3 mr-1 fill-current" />
                  Top Match
                </Badge>
              </div>
            )}
            
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">{path.title}</h3>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  {path.description}
                </p>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Match Score</span>
                    <span className="text-sm font-medium">{path.matchScore}%</span>
                  </div>
                  <Progress value={path.matchScore} className="h-2" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Timeline: {path.timeframe}</span>
                  </div>
                  
                  {path.salary && (
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {formatSalary(path.salary.min, path.salary.max, path.salary.currency)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-1">
                    {path.skills.map((skill, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Key Milestones</h4>
                  <ul className="space-y-2">
                    {path.milestones.map((milestone, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>{milestone}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button 
                  className="w-full" 
                  onClick={() => onExplore?.(path.id)}
                  asChild
                >
                  <Link to={`/planner/path/${path.id}`}>
                    Explore Path
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}